/**
 * 风格卡片管理 Tab（管理后台 → 画布分组）
 *
 * 列出所有风格卡片（含待审核），支持编辑（名称/prompt/分类/排序）、审核（通过/拒绝）、删除。
 */

import { useEffect, useState } from 'react';
import { Loader2, Check, X, Trash2, Pencil, RotateCw } from 'lucide-react';
import toast from '../../Toast';
import { platformFetch } from '../../../platform/http';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';

interface StyleCard {
  id: string;
  name: string;
  prompt: string;
  coverUrl: string | null;
  category: string;
  sortOrder: number;
  status: string;
  submitterId: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: '待审核', color: 'bg-amber-100 text-amber-700' },
  approved: { label: '已上线', color: 'bg-green-100 text-green-700' },
  rejected: { label: '已拒绝', color: 'bg-red-100 text-red-700' },
};

export default function CanvasStyleCardsTab() {
  const [cards, setCards] = useState<StyleCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<StyleCard | null>(null);
  const [editForm, setEditForm] = useState({ name: '', prompt: '', category: '', sortOrder: 0 });

  const fetchCards = async () => {
    try {
      const res = await platformFetch('/admin/canvas/style-cards');
      const data = await res.json();
      setCards(data?.data ?? []);
    } catch {
      toast.error('加载风格卡片失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCards(); }, []);

  const startEdit = (card: StyleCard) => {
    setEditing(card);
    setEditForm({ name: card.name, prompt: card.prompt, category: card.category, sortOrder: card.sortOrder });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const res = await platformFetch(`/admin/canvas/style-cards/${editing.id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error('保存失败');
      toast.success('已保存');
      setEditing(null);
      fetchCards();
    } catch {
      toast.error('保存失败');
    }
  };

  const review = async (id: string, status: 'approved' | 'rejected') => {
    try {
      const res = await platformFetch(`/admin/canvas/style-cards/${id}/review`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('操作失败');
      toast.success(status === 'approved' ? '已通过' : '已拒绝');
      fetchCards();
    } catch {
      toast.error('操作失败');
    }
  };

  const remove = async (id: string) => {
    if (!confirm('确认删除该风格卡片？')) return;
    try {
      const res = await platformFetch(`/admin/canvas/style-cards/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('删除失败');
      toast.success('已删除');
      fetchCards();
    } catch {
      toast.error('删除失败');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">风格卡片管理</h2>
        <button
          type="button"
          onClick={fetchCards}
          className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition hover:bg-slate-50"
        >
          <RotateCw className="h-3.5 w-3.5" />
          刷新
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">
          暂无风格卡片
        </div>
      ) : (
        <div className="space-y-2">
          {cards.map((card) => {
            const statusInfo = STATUS_LABELS[card.status] ?? { label: card.status, color: 'bg-slate-100 text-slate-600' };
            const isEditing = editing?.id === card.id;
            return (
              <div
                key={card.id}
                className="flex items-start gap-3 rounded-lg border border-slate-200 p-3"
              >
                {/* 封面 */}
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  {card.coverUrl ? (
                    <img
                      src={resolveBrowserMediaUrl(card.coverUrl) || card.coverUrl}
                      alt={card.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-300">
                      <Pencil className="h-5 w-5" />
                    </div>
                  )}
                </div>

                {/* 内容 */}
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          placeholder="名称"
                          className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-fuchsia-300"
                        />
                        <input
                          type="text"
                          value={editForm.category}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                          placeholder="分类"
                          className="w-24 rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-fuchsia-300"
                        />
                        <input
                          type="number"
                          value={editForm.sortOrder}
                          onChange={(e) => setEditForm({ ...editForm, sortOrder: Number(e.target.value) })}
                          placeholder="排序"
                          className="w-16 rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-fuchsia-300"
                        />
                      </div>
                      <textarea
                        value={editForm.prompt}
                        onChange={(e) => setEditForm({ ...editForm, prompt: e.target.value })}
                        rows={3}
                        className="w-full resize-none rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-fuchsia-300"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={saveEdit}
                          className="flex items-center gap-1 rounded bg-fuchsia-500 px-3 py-1 text-xs font-medium text-white hover:bg-fuchsia-600"
                        >
                          <Check className="h-3 w-3" />
                          保存
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="rounded px-3 py-1 text-xs text-slate-500 hover:bg-slate-100"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{card.name}</span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                          {card.category}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{card.prompt}</p>
                    </>
                  )}
                </div>

                {/* 操作 */}
                {!isEditing && (
                  <div className="flex shrink-0 flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(card)}
                      title="编辑"
                      className="grid h-7 w-7 place-items-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {card.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          onClick={() => review(card.id, 'approved')}
                          title="通过"
                          className="grid h-7 w-7 place-items-center rounded text-green-500 transition hover:bg-green-50"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => review(card.id, 'rejected')}
                          title="拒绝"
                          className="grid h-7 w-7 place-items-center rounded text-red-400 transition hover:bg-red-50"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(card.id)}
                      title="删除"
                      className="grid h-7 w-7 place-items-center rounded text-red-400 transition hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
